// src/utils/distance.js
// Utilitaires pour calculer les distances - NE PAS MODIFIER

/**
 * Calcule la distance en km entre deux points géographiques (Formule Haversine)
 * @param {number} lat1 - Latitude du point 1
 * @param {number} lng1 - Longitude du point 1
 * @param {number} lat2 - Latitude du point 2
 * @param {number} lng2 - Longitude du point 2
 * @returns {number} Distance en km
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
  
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
  
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
  
    return Math.round(distance * 100) / 100; // Arrondir à 2 décimales
  };
  
  /**
   * Convertir des degrés en radians
   * @param {number} deg - Valeur en degrés
   * @returns {number} Valeur en radians
   */
  const toRad = (deg) => {
    return (deg * Math.PI) / 180;
  };
  
  /**
   * Formater la distance pour l'affichage
   * @param {number} distance - Distance en km
   * @returns {string} Distance formatée (ex: "2.5 km" ou "250 m")
   */
  export const formatDistance = (distance) => {
    if (distance < 1) {
      // Afficher en mètres
      const meters = Math.round(distance * 1000);
      return `${meters} m`;
    }
    // Afficher en km
    return `${distance.toFixed(1)} km`;
  };
  
  /**
   * Obtenir une description lisible de la distance
   * @param {number} distance - Distance en km
   * @returns {string} Description (ex: "à proximité", "proche", "loin")
   */
  export const getDistanceDescription = (distance) => {
    if (distance < 0.5) return 'À proximité';
    if (distance < 1) return 'Très proche';
    if (distance < 2) return 'Proche';
    if (distance < 5) return 'À proximité';
    if (distance < 10) return 'Modérément proche';
    if (distance < 20) return 'À une certaine distance';
    return 'Loin';
  };
  
  /**
   * Vérifier si deux points sont dans le même rayon
   * @param {number} lat1 - Latitude du point 1
   * @param {number} lng1 - Longitude du point 1
   * @param {number} lat2 - Latitude du point 2
   * @param {number} lng2 - Longitude du point 2
   * @param {number} radiusKm - Rayon en km
   * @returns {boolean} true si les points sont dans le rayon
   */
  export const isWithinRadius = (lat1, lng1, lat2, lng2, radiusKm = 50) => {
    const distance = calculateDistance(lat1, lng1, lat2, lng2);
    return distance <= radiusKm;
  };
  
  /**
   * Trier un tableau d'annonces par distance depuis un point
   * @param {Array} listings - Tableau d'annonces
   * @param {number} lat - Latitude du point de référence
   * @param {number} lng - Longitude du point de référence
   * @returns {Array} Annonces triées par distance
   */
  export const sortByDistance = (listings, lat, lng) => {
    return [...listings].sort((a, b) => {
      if (!a.lat || !a.lng || !b.lat || !b.lng) return 0;
      const distA = calculateDistance(lat, lng, a.lat, a.lng);
      const distB = calculateDistance(lat, lng, b.lat, b.lng);
      return distA - distB;
    });
  };
  
  /**
   * Filtrer les annonces par distance
   * @param {Array} listings - Tableau d'annonces
   * @param {number} lat - Latitude du point de référence
   * @param {number} lng - Longitude du point de référence
   * @param {number} radiusKm - Rayon en km
   * @returns {Array} Annonces filtrées
   */
  export const filterByDistance = (listings, lat, lng, radiusKm = 50) => {
    return listings.filter((listing) => {
      if (!listing.lat || !listing.lng) return false;
      return isWithinRadius(lat, lng, listing.lat, listing.lng, radiusKm);
    });
  };
  
  /**
   * Calculer le centre géographique d'un ensemble de points
   * @param {Array} listings - Tableau d'annonces avec lat/lng
   * @returns {Object} Centre avec lat et lng
   */
  export const calculateCenter = (listings) => {
    if (listings.length === 0) return { lat: 0, lng: 0 };
  
    const validListings = listings.filter((l) => l.lat && l.lng);
    if (validListings.length === 0) return { lat: 0, lng: 0 };
  
    const avgLat = validListings.reduce((sum, l) => sum + l.lat, 0) / validListings.length;
    const avgLng = validListings.reduce((sum, l) => sum + l.lng, 0) / validListings.length;
  
    return { lat: avgLat, lng: avgLng };
  };
  
  /**
   * Obtenir les limites géographiques (bounds) d'un ensemble de points
   * @param {Array} listings - Tableau d'annonces avec lat/lng
   * @returns {Object} Limites avec south, north, west, east
   */
  export const calculateBounds = (listings) => {
    if (listings.length === 0) {
      return { south: 0, north: 0, west: 0, east: 0 };
    }
  
    const validListings = listings.filter((l) => l.lat && l.lng);
    if (validListings.length === 0) {
      return { south: 0, north: 0, west: 0, east: 0 };
    }
  
    const lats = validListings.map((l) => l.lat);
    const lngs = validListings.map((l) => l.lng);
  
    return {
      south: Math.min(...lats),
      north: Math.max(...lats),
      west: Math.min(...lngs),
      east: Math.max(...lngs),
    };
  };
  
  /**
   * Créer les coordonnées d'une zone circulaire
   * @param {number} centerLat - Latitude du centre
   * @param {number} centerLng - Longitude du centre
   * @param {number} radiusKm - Rayon en km
   * @param {number} points - Nombre de points (défaut 32)
   * @returns {Array} Tableau de coordonnées
   */
  export const createCircleCoordinates = (centerLat, centerLng, radiusKm, points = 32) => {
    const coordinates = [];
    const R = 6371; // Rayon de la Terre
    const angularRadius = radiusKm / R;
  
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * (2 * Math.PI);
      const lat = Math.asin(
        Math.sin(toRad(centerLat)) * Math.cos(angularRadius) +
          Math.cos(toRad(centerLat)) * Math.sin(angularRadius) * Math.cos(angle)
      );
      const lng =
        toRad(centerLng) +
        Math.atan2(
          Math.sin(angle) * Math.sin(angularRadius) * Math.cos(toRad(centerLat)),
          Math.cos(angularRadius) - Math.sin(toRad(centerLat)) * Math.sin(lat)
        );
  
      coordinates.push({
        lat: (lat * 180) / Math.PI,
        lng: (lng * 180) / Math.PI,
      });
    }
  
    return coordinates;
  };
  
  /**
   * Vérifier si un point est dans un polygone
   * @param {number} lat - Latitude du point
   * @param {number} lng - Longitude du point
   * @param {Array} polygon - Tableau de points avec lat/lng
   * @returns {boolean} true si le point est dans le polygone
   */
  export const isPointInPolygon = (lat, lng, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lng;
      const yi = polygon[i].lat;
      const xj = polygon[j].lng;
      const yj = polygon[j].lat;
  
      const intersect = yi > lng !== yj > lng && lng < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };